import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import { StyledGridCol, StyledGridRow } from "@/common/styles";
import { setBookings } from "@/slices/bookingsSlice";
import {
  getOptionsFromRefList,
  getRefListNameByID,
  findAgefromDOB,
} from "@/util/common_function";
import { Form, message } from "antd";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";

const InmateInformation = forwardRef(({ inmateInfoRef }, ref) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { bookings, bookingsFieldsEnabled } = useSelector(
    (state) => state?.bookings
  );
  const [formData, setFormData] = useState(null);
  const fullSearch = useSelector(state => state.fullSearch);

  useEffect(() => {
      let inmateInfo;
      let usingCharges = false;
      // Check if there are any charges
      if (bookings?.charges?.length > 0) {
        // Find the first charge where controllingCharge is true and clearedToGo is not true
        let chargeCandidate = bookings.charges.find(
          (charge) => charge.controllingCharge === true && charge.clearedToGo !== true
        );
        // If no such charge is found, then (if all charges are cleared) find a charge where controllingCharge is true
        if (!chargeCandidate) {
          chargeCandidate = bookings.charges.find(
            (charge) => charge.controllingCharge === true
          );
        }
        console.log(bookings.charges)
        if (chargeCandidate) {
          inmateInfo = chargeCandidate;
          usingCharges = true;
        }
      }
      // If no charge candidate is found, then check arrestReports
      if (bookings?.arrestReports?.length > 0) {
        inmateInfo = {
          ...bookings.arrestReports[0],
          ...inmateInfo
        }
      }
      
      if (inmateInfo) {
        setFormData({
          holdReasonId: usingCharges
            ? inmateInfo.controllingCharge
              ? 154530006
              : 154530013
            : inmateInfo.holdReasonId
            ? inmateInfo.controllingCharge
              ? 154530006
              : inmateInfo.holdReasonId
            : 154530013,
          controllingCharge: usingCharges
            ? getRefListNameByID("Charges", inmateInfo?.chargeDescriptionID)
            : inmateInfo.controllingCharge,
          inmateStatusId: inmateInfo.inmateStatusId,
          releaseToFacilityId: inmateInfo.releaseToFacilityId,
          communityServiceHoursSentenced: inmateInfo.communityServiceHoursSentenced,
          communityServiceHoursWorked: inmateInfo.communityServiceHoursWorked,
          inmateTypeId: inmateInfo.inmateTypeId,
          controllingAgencyCaseNumber: usingCharges
            ? inmateInfo.caseNumber
            : inmateInfo.controllingAgencyCaseNumber,
          controllingAgencyOriId: usingCharges
            ? inmateInfo.controllingAgencyORIID
            : inmateInfo.controllingAgencyOriId,
        });
      } else {
        setFormData({
          holdReasonId: fullSearch ? null : 154530013,
          controllingCharge: null,
          inmateStatusId: null,
          releaseToFacilityId: null,
          communityServiceHoursSentenced: null,
          communityServiceHoursWorked: null,
          inmateTypeId: null,
          controllingAgencyCaseNumber: null,
          controllingAgencyOriId: null,
        });
      }

      if (bookings?.bio?.[0]?.dob && !(inmateInfo?.inmateTypeId)) {
        let age = findAgefromDOB(bookings?.bio?.[0]?.dob);
        let iTyp = age < 18 ? 154540005 : 154540001;
        setFormData({
          inmateTypeId: iTyp,
        }); 
      }
    
  }, [bookings]);
  
  useEffect(() => {
    // Update form fields only when formData changes initially.
    if (formData !== null) {
      form.setFieldsValue(formData);
    } else {
      form.resetFields();
    }
  }, [formData]);

  useImperativeHandle(inmateInfoRef, () => ({
    validateFields: () => form.validateFields(),
    resetForm: () => form.resetFields(),
    getImgData: () => imgData,
    setImgDataEmpty: () => setImgData([]),
    setSelectedImgEmpty: () => setSelectedImg(null),
  }));

  const updateStore = debounce((data) => {
    dispatch(
      setBookings({
        arrestReports: [
          {
            ...bookings?.arrestReports?.[0],
            [Object.keys(data)[0]]: data[Object.keys(data)[0]],
          },
        ],
      })
    );
  }, 800);

  return (
    <>
      <Form
        form={form}
        initialValues={formData}
        disabled={!bookingsFieldsEnabled}
        onValuesChange={updateStore}
      >
        <StyledGridRow gutter={16}>
          <StyledGridCol span={8}>
            <FormItem
              label="Hold Reason"
              name="holdReasonId"
              required={!fullSearch}
              rules={
                !fullSearch
                ? [
                  {
                    required: true,
                    message: "Hold Reason is required",
                  },
                ] : []
              }
            >
              <Select
                id="IF-HoldReason"
                options={getOptionsFromRefList(
                  "Inmate_Hold_Reason",
                  false,
                  false,
                  true
                )}
                placeholder="Hold Reason"
                variant="outlined"
                disabled={!bookingsFieldsEnabled}
              />
            </FormItem>
          </StyledGridCol>
          <StyledGridCol span={8}>
            <FormItem label="Inmate Status" name="inmateStatusId">
              <Select
                id="IF-InmateStatus"
                options={getOptionsFromRefList(
                  "Inmate_Status",
                  false,
                  false,
                  true
                )}
                placeholder="Inmate Status"
                variant="outlined"
                disabled={!bookingsFieldsEnabled}
              />
            </FormItem>
          </StyledGridCol>
          <StyledGridCol span={8}>
            <FormItem label="Facility Status" name="releaseToFacilityId">
              <Select
                id="IF-FacilityStatus"
                options={getOptionsFromRefList(
                  "Workhouse_Status",
                  false,
                  false,
                  true
                )}
                placeholder="Facility Status"
                variant="outlined"
                disabled={!bookingsFieldsEnabled}
              />
            </FormItem>
          </StyledGridCol>
        </StyledGridRow>
        <StyledGridRow gutter={16}>
          <StyledGridCol span={8}>
            <FormItem
              label="Comm. Svc. Hours Sentenced"
              name="communityServiceHoursSentenced"
            >
              <TextField type="number" />
            </FormItem>
          </StyledGridCol>
          <StyledGridCol span={8}>
            <FormItem
              label="Comm. Svc. Hours Worked"
              name="communityServiceHoursWorked"
            >
              <TextField type="number" />
            </FormItem>
          </StyledGridCol>
          <StyledGridCol span={8}>
            <FormItem label="Inmate Type" name="inmateTypeId">
              <Select
                id="IF-InmateType"
                options={getOptionsFromRefList("Inmate_Type", false, false, true)}
                placeholder="Inmate Type"
                variant="outlined"
                disabled={!bookingsFieldsEnabled}
              />
            </FormItem>
          </StyledGridCol>
        </StyledGridRow>
        <StyledGridRow gutter={16}>
          <StyledGridCol span={8}>
            <FormItem
              label="Controlling Agency Case #"
              name="controllingAgencyCaseNumber"
            >
              <TextField id="IF-ControllingAgencyCase" disabled={!fullSearch}  />
            </FormItem>
          </StyledGridCol>
          <StyledGridCol span={8}>
            <FormItem
              label="Controlling Agency ORI"
              name="controllingAgencyOriId"
            >
              <Select
                id="IF-ControllingAgencyORI"
                options={getOptionsFromRefList(
                  "Controlling_Agency",
                  false,
                  true,
                  true
                )}
                placeholder="Controlling Agency ORI"
                showSearch
                variant="outlined"
                disabled={!bookingsFieldsEnabled}
              />
            </FormItem>
          </StyledGridCol>
          <StyledGridCol span={8}>
            <FormItem label="Controlling Charge" name="controllingCharge">
              <TextField disabled={!fullSearch} />
            </FormItem>
          </StyledGridCol>
        </StyledGridRow>
      </Form>
    </>
  );
});
export default InmateInformation;
